
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiChevronDown,
  HiPencil,
  HiTrash,
  HiArrowLeft,
  HiUpload,
  HiChevronLeft,
  HiChevronRight,
  HiInformationCircle,
  HiX,
} from 'react-icons/hi';
import { fetchModuleList, createModule, updateModule, deleteModule } from '../../services/moduleService';
import { fetchModuleGroupingList } from '../../services/moduleGroupingService';
import ModuleNav from './ModuleNav';
import {
  DASHBOARD_MODULE_TAG_OPTIONS,
  toggleDashboardModuleTag,
  dashboardModuleTagLabel,
} from '../../constants/dashboardModuleTags';
import { uploadQuizCsv } from '../../services/quizService';
import { invalidateChaptersCache } from '../chapters/chaptersCache';
import { invalidateLessonCache } from '../lesson/lessonCache';
import { invalidateLessonContentCache } from '../lessonContent/lessonContentCache';
import { moduleCache } from './moduleCache';
import Toast from '../../components/toaster';
import { uploadImage } from '../../services/assetService';
import EditOrderIcon from '../../assets/icons/edit-order.svg';
import UploadCsvIcon from '../../assets/icons/upload-csv.svg';
//import { HiChevronDown } from 'react-icons/hi';

import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


const formatJsonPath = (jsonPath) => {
  if (!jsonPath || typeof jsonPath !== 'string') return '';

  // Example:
  // "$[0].chapters[0].units[1].lessons[0].questions[4].options"
  // -> "chapters-1, units-2, lessons-1, questions-5.option"
  const parts = [];
  const re = /\.(chapters|units|lessons|questions)\[(\d+)\]/g;
  let match;
  while ((match = re.exec(jsonPath)) !== null) {
    const key = match[1];
    const idx = Number(match[2]);
    const n = Number.isFinite(idx) ? idx + 1 : match[2];
    parts.push(`${key}-${n}`);
  }

  const trailingKeyMatch = jsonPath.match(/\.([a-zA-Z_]+)$/);
  const trailingKeyRaw = trailingKeyMatch?.[1] || '';
  const trailingKey = trailingKeyRaw === 'options' ? 'option' : trailingKeyRaw;

  if (parts.length === 0) return trailingKey ? trailingKey : jsonPath;
  return `${parts.join(', ')}${trailingKey ? `.${trailingKey}` : ''}`;
};

const sameTagSet = (left, right) => {
  const a = [...(left || [])].sort();
  const b = [...(right || [])].sort();
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

const toGroupingIds = (raw) => {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((item) => String(item && item._id ? item._id : item)).filter(Boolean))];
};

const sameIdSet = (left, right) => {
  const a = toGroupingIds(left).sort();
  const b = toGroupingIds(right).sort();
  return a.length === b.length && a.every((value, index) => value === b[index]);
};

const toggleGroupingId = (selected, id) => {
  const sid = String(id);
  return selected.includes(sid) ? selected.filter((value) => value !== sid) : [...selected, sid];
};

/** Sessions Program Categories picker — chips + searchable checkbox list, not a plain checkbox column. */
const ModuleTagsField = ({ tags, onChange, isEditing = false }) => {
  const [query, setQuery] = useState('');
  const originalRef = useRef(Array.isArray(tags) ? [...tags] : []);
  const selected = Array.isArray(tags) ? tags : [];
  const selectedCount = selected.length;
  const isDirty = isEditing && !sameTagSet(selected, originalRef.current);
  const q = query.trim().toLowerCase();
  const options = DASHBOARD_MODULE_TAG_OPTIONS.filter(
    (option) => !q || option.label.toLowerCase().includes(q)
  );

  return (
    <fieldset className="rounded-xl border border-gray-200 bg-gray-50/40 p-3 space-y-3">
      <legend className="sr-only">Dashboard tags</legend>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-semibold text-gray-800 px-0.5" aria-hidden>
          Dashboard tags
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-600">
            {selectedCount} selected
          </span>
          {isEditing && isDirty && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand/10 text-brand border border-brand/20">
              Edited
            </span>
          )}
          {isEditing && !isDirty && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-400">
              Unchanged
            </span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-gray-200 bg-white min-h-[2.75rem] p-2">
        {selectedCount === 0 ? (
          <p className="text-xs text-gray-400 px-1 py-1.5">
            No dashboard tags selected yet — pick from the list below.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((tag) => {
              const label = dashboardModuleTagLabel(tag);
              return (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 max-w-full pl-1.5 pr-1 py-1 rounded-lg text-xs border bg-gray-100 text-gray-800 border-gray-200"
                >
                  <span className="truncate">{label}</span>
                  <button
                    type="button"
                    onClick={() => onChange(toggleDashboardModuleTag(selected, tag))}
                    className="p-0.5 rounded hover:bg-gray-200"
                    aria-label={`Remove ${label}`}
                  >
                    <HiX className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="relative">
        <HiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search dashboard tags…"
          className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400"
          aria-label="Search dashboard tags"
        />
      </div>

      <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white">
        {options.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">No matches for “{query.trim()}”</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {options.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors ${
                    checked ? 'bg-brand/5 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(toggleDashboardModuleTag(selected, option.value))}
                    className="rounded border-gray-300 text-brand focus:ring-brand/30"
                  />
                  <span className="truncate flex-1">{option.label}</span>
                  {checked && (
                    <span className="text-[10px] font-medium text-brand flex-shrink-0">Added</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </fieldset>
  );
};

const ModuleGroupingsField = ({ groupingIds, options = [], onChange, isEditing = false }) => {
  const [query, setQuery] = useState('');
  const originalRef = useRef(toGroupingIds(groupingIds));
  const selected = toGroupingIds(groupingIds);
  const selectedCount = selected.length;
  const isDirty = isEditing && !sameIdSet(selected, originalRef.current);
  const q = query.trim().toLowerCase();
  const labelFor = (id) => {
    const match = options.find((option) => String(option._id) === String(id));
    if (!match) return id;
    return match.is_active === false ? `${match.name} (Inactive)` : match.name;
  };
  const filtered = options.filter((option) => !q || String(option.name || '').toLowerCase().includes(q));

  return (
    <fieldset className="rounded-xl border border-gray-200 bg-gray-50/40 p-3 space-y-3">
      <legend className="sr-only">Module groupings</legend>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-semibold text-gray-800 px-0.5" aria-hidden>
          Module groupings
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-600">
            {selectedCount} selected
          </span>
          {isEditing && isDirty && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-brand/10 text-brand border border-brand/20">
              Edited
            </span>
          )}
          {isEditing && !isDirty && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-400">
              Unchanged
            </span>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-gray-200 bg-white min-h-[2.75rem] p-2">
        {selectedCount === 0 ? (
          <p className="text-xs text-gray-400 px-1 py-1.5">
            No groupings selected yet — pick from the list below. Used only for Because you.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selected.map((id) => {
              const label = labelFor(id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 max-w-full pl-1.5 pr-1 py-1 rounded-lg text-xs border bg-gray-100 text-gray-800 border-gray-200"
                >
                  <span className="truncate">{label}</span>
                  <button
                    type="button"
                    onClick={() => onChange(toggleGroupingId(selected, id))}
                    className="p-0.5 rounded hover:bg-gray-200"
                    aria-label={`Remove ${label}`}
                  >
                    <HiX className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="relative">
        <HiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search module groupings…"
          className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400"
          aria-label="Search module groupings"
        />
      </div>

      <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white">
        {options.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">No groupings yet — add them on the Groupings tab.</p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-gray-400 px-3 py-3">No matches for “{query.trim()}”</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((option) => {
              const id = String(option._id);
              const checked = selected.includes(id);
              return (
                <label
                  key={id}
                  className={`flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors ${
                    checked ? 'bg-brand/5 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(toggleGroupingId(selected, id))}
                    className="rounded border-gray-300 text-brand focus:ring-brand/30"
                  />
                  <span className="truncate flex-1">{option.name}</span>
                  {option.is_active === false && (
                    <span className="text-[10px] font-medium text-gray-400 flex-shrink-0">Inactive</span>
                  )}
                  {checked && (
                    <span className="text-[10px] font-medium text-brand flex-shrink-0">Added</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
      </div>
    </fieldset>
  );
};

/** Sessions-style filter dropdown — not native system <select>. */
const ModuleTagFilterSelect = ({ value, open, onToggle, onSelect }) => {
  const options = [{ value: '', label: 'All tags' }, ...DASHBOARD_MODULE_TAG_OPTIONS];
  const selected = options.find((option) => option.value === value) || options[0];

  return (
    <div className="relative w-full md:w-44 module-tag-filter-dropdown">
      <button
        type="button"
        id="module-tag-filter"
        onClick={onToggle}
        className="w-full h-10 pl-3 pr-10 text-sm text-left leading-normal border border-[#ECECEC] rounded-xl focus:outline-none bg-white truncate"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Tag filter"
      >
        {selected.label}
      </button>
      <HiChevronDown
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${
          open ? 'rotate-180' : ''
        }`}
        aria-hidden
      />
      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200"
          role="listbox"
          aria-labelledby="module-tag-filter"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value || 'all'}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onSelect(option.value)}
                className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-100 truncate ${
                  isSelected ? 'bg-gray-50 font-semibold' : 'text-gray-900'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Module = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [editingUploadingImage, setEditingUploadingImage] = useState(false);
  const [editingUploadError, setEditingUploadError] = useState('');
  const [openSection, setOpenSection] = useState(null);
  const [newModule, setNewModule] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    order: 1,
    tags: [],
    grouping_ids: [],
    image: null,
    imagePreview: null,
    imageSource: 'file',
  });
  const [addModuleErrors, setAddModuleErrors] = useState({
    title: '',
    description: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingModule, setEditingModule] = useState({
    id: null,
    module_id: null,
    title: '',
    description: '',
    thumbnail_url: '',
    order: 1,
    is_active: true,
    tags: [],
    grouping_ids: [],
    image: null,
    imagePreview: null,
    imageSource: 'file',
  });


  // API state
  const [modules, setModules] = useState(moduleCache.modules || []);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(moduleCache.page || 1);
  const [limit] = useState(16);
  const [totalPages, setTotalPages] = useState(moduleCache.totalPages || 1);
  const [totalCount, setTotalCount] = useState(moduleCache.totalCount || 0);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [uploadResult, setUploadResult] = useState({
    successful: [],
    skipped: [],
    error: [],
  });
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [hasAnyModules, setHasAnyModules] = useState(true);
  const [tagFilter, setTagFilter] = useState('');
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [groupingOptions, setGroupingOptions] = useState([]);

  // Tooltip state
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipWidth, setTooltipWidth] = useState(400);
  const firstCardRef = useRef(null);

  // Fetch modules from API
  const loadModules = async (currentPage = page, currentSearch = searchQuery, forceRefresh = false, silent = false) => {
    const isFirstLoad = moduleCache.firstLoad;
    const isSearchChange = currentSearch !== (moduleCache.lastSearch || '');
    const isPageChange = currentPage !== (moduleCache.page || 1);

    if (!silent && (isFirstLoad || isSearchChange || isPageChange || forceRefresh)) {
      setIsLoading(true);
    }

    try {
      const encodedSearch = encodeURIComponent(currentSearch || '');
      const response = await fetchModuleList(
        currentPage,
        limit,
        encodedSearch,
        currentSearch,
        tagFilter ? { tags: tagFilter } : {}
      );

      // Set loading to false immediately after API resolves
      if (!silent) setIsLoading(false);

      let allModulesData = [];
      let total = 0;
      let totalPagesData = 1;

      // Adjust based on actual API response structure
      if (response.data && Array.isArray(response.data)) {
        allModulesData = response.data;
        total = response.total || response.data.length;
        totalPagesData = response.totalPages || Math.ceil((response.total || response.data.length) / limit);
      } else if (Array.isArray(response)) {
        allModulesData = response;
        total = response.length;
        totalPagesData = response.totalPages || Math.ceil(response.length / limit);
      } else {
        // Handle different response structures
        allModulesData = response.modules || response.results || [];
        total = response.total || 0;
        totalPagesData = response.totalPages || Math.ceil((response.total || 0) / limit);
      }

      if (tagFilter) {
        allModulesData = allModulesData.filter(
          (module) => Array.isArray(module.tags) && module.tags.includes(tagFilter)
        );
      }

      // Check if API handles pagination server-side
      // If response has totalPages or total that doesn't match array length, assume server-side pagination
      const hasServerPagination = (response.totalPages !== undefined && response.totalPages !== null) ||
        (response.total !== undefined && response.total !== null && response.total !== allModulesData.length);

      let modulesData = allModulesData;
      const useClientPagination = !hasServerPagination && allModulesData.length > limit;

      if (useClientPagination) {
        // Client-side pagination: slice the data based on current page
        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;
        modulesData = allModulesData.slice(startIndex, endIndex);
        total = allModulesData.length;
        totalPagesData = Math.ceil(allModulesData.length / limit);
      }

      setModules(modulesData);
      setTotalCount(total);
      setTotalPages(totalPagesData);

      if (!currentSearch) {
        setHasAnyModules(total > 0 || modulesData.length > 0);
      } else if (total > 0 || modulesData.length > 0) {
        setHasAnyModules(true);
      }

      // Store all modules for client-side pagination if needed
      moduleCache.allModules = useClientPagination ? allModulesData : null;
      moduleCache.modules = modulesData;
      moduleCache.useClientPagination = useClientPagination;
      moduleCache.page = currentPage;
      moduleCache.totalPages = totalPagesData;
      moduleCache.totalCount = total;
      moduleCache.lastSearch = currentSearch;
      moduleCache.firstLoad = false;
    } catch (error) {
      if (error?.response?.status === 401) {
        return; // silent like Feedback page
      }
      // console.error('Error fetching modules:', error);
      setModules([]);
      moduleCache.firstLoad = false;
    } finally {
      if (!silent) setIsLoading(false);
    }

  };

  //Reorder modules
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderModules, setReorderModules] = useState([]);
  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);

  // Cleanup: Restore scrolling when modal closes
  useEffect(() => {
    if (!showReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showReorderModal]);

  useEffect(() => {
    let cancelled = false;
    fetchModuleGroupingList(1, 200, '')
      .then((res) => {
        if (!cancelled) setGroupingOptions(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setGroupingOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Guard flags to prevent duplicate API calls
  const isInitialMountRef = useRef(true);
  const isLoadingRef = useRef(false);
  const searchDebounceTimerRef = useRef(null);
  const lastCallParamsRef = useRef({ page: null, search: null, tag: null });
  const loadingDelayTimerRef = useRef(null);
  const paginationDelayTimerRef = useRef(null);
  const paginationInitialSkipRef = useRef(true);

  // Debounce search query - only run when searchQuery changes, not on initial mount
  useEffect(() => {
    // Skip debouncing on initial mount
    if (isInitialMountRef.current) {
      return;
    }

    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to first page when search changes (page is accessed from closure)
      setPage((currentPage) => currentPage !== 1 ? 1 : currentPage);
    }, 500); // 500ms debounce

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]); // Only react to searchQuery changes

  // Load modules on mount and when page/debounced search changes - single useEffect for API call
  useEffect(() => {
    const currentParams = { page, search: debouncedSearch, tag: tagFilter };
    const lastParams = lastCallParamsRef.current;

    const isSearchChange = debouncedSearch !== (moduleCache.lastSearch || '');
    const hasCache = moduleCache.allModules !== null && !moduleCache.firstLoad && !isSearchChange && !tagFilter;
    const useClientPagination = moduleCache.useClientPagination;

    // If search hasn't changed and we're using client-side pagination, slice from cache
    if (hasCache && useClientPagination && !isSearchChange && !tagFilter && moduleCache.allModules.length > limit) {
      if (loadingDelayTimerRef.current) {
        clearTimeout(loadingDelayTimerRef.current);
        loadingDelayTimerRef.current = null;
      }
      const startIndex = (currentParams.page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedModules = moduleCache.allModules.slice(startIndex, endIndex);
      const cachedTotalPages = Math.ceil(moduleCache.allModules.length / limit);

      setModules(paginatedModules);
      setTotalCount(moduleCache.allModules.length);
      setTotalPages(cachedTotalPages);
      lastCallParamsRef.current = currentParams;
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }

      // Background revalidate (silent) so counts update after navigating back from child pages.
      setTimeout(() => {
        loadModules(currentParams.page, debouncedSearch, true, true);
      }, 0);
      return;
    }

    // Use cached data only if it's the exact same page and search (initial mount)
    if (hasCache && isInitialMountRef.current && currentParams.page === moduleCache.page && currentParams.search === (moduleCache.lastSearch || '')) {
      let cachedModules = moduleCache.modules;
      if (moduleCache.allModules && moduleCache.allModules.length > limit) {
        const startIndex = (currentParams.page - 1) * limit;
        const endIndex = startIndex + limit;
        cachedModules = moduleCache.allModules.slice(startIndex, endIndex);
      }
      setModules(cachedModules);
      setPage(moduleCache.page);
      setTotalPages(moduleCache.totalPages);
      setTotalCount(moduleCache.totalCount);
      lastCallParamsRef.current = currentParams;
      isInitialMountRef.current = false;

      // Background revalidate (silent) so counts update after navigating back from child pages.
      setTimeout(() => {
        loadModules(currentParams.page, debouncedSearch, true, true);
      }, 0);
      return;
    }

    // Prevent duplicate calls with same parameters
    // This handles React StrictMode double-invocation and unnecessary re-renders
    const isSameParams = lastParams.page === currentParams.page &&
      lastParams.search === currentParams.search &&
      lastParams.tag === currentParams.tag;

    // Block if same parameters (except on very first mount)
    if (isSameParams) {
      // On initial mount, allow first call even if params match (they're both initial values)
      if (isInitialMountRef.current) {
        // But check if we've already set the params (React StrictMode second call)
        if (lastParams.page !== null) {
          return; // Already called with these params, skip
        }
      } else {
        // After initial mount, always block same params
        return;
      }
    }

    // Prevent concurrent API calls
    if (isLoadingRef.current) {
      return;
    }

    // Mark parameters as used immediately (before async call)
    lastCallParamsRef.current = currentParams;
    isLoadingRef.current = true;

    loadModules(page, debouncedSearch).finally(() => {
      isLoadingRef.current = false;
      // Mark initial mount as complete after first API call
      if (isInitialMountRef.current) {
        isInitialMountRef.current = false;
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, tagFilter]);

  // Full-page loader on page change (covers fast server responses; pairs with client-side slice loader)
  useEffect(() => {
    if (paginationInitialSkipRef.current) {
      paginationInitialSkipRef.current = false;
      return;
    }
    if (paginationDelayTimerRef.current) {
      clearTimeout(paginationDelayTimerRef.current);
      paginationDelayTimerRef.current = null;
    }
    setPaginationLoading(true);
    paginationDelayTimerRef.current = setTimeout(() => {
      setPaginationLoading(false);
      paginationDelayTimerRef.current = null;
    }, 200);
    return () => {
      if (paginationDelayTimerRef.current) {
        clearTimeout(paginationDelayTimerRef.current);
        paginationDelayTimerRef.current = null;
      }
    };
  }, [page]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.status-dropdown-container')) {
        setOpenDropdownId(null);
      }
      if (tagFilterOpen && !event.target.closest('.module-tag-filter-dropdown')) {
        setTagFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId, tagFilterOpen]);

  // Update tooltip position
  const updateTooltipPosition = useCallback(() => {
    if (firstCardRef.current && showTooltip) {
      const rect = firstCardRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const cardWidth = rect.width;

      // Calculate tooltip width based on card width
      // Tooltip should be slightly wider than the card, but with min/max constraints
      let calculatedWidth = cardWidth + 40; // Add 40px padding to card width

      // Apply min and max constraints based on screen size
      const minWidth = viewportWidth < 768 ? 200 : viewportWidth < 1024 ? 280 : 320;
      const maxWidth = viewportWidth < 768 ? 280 : viewportWidth < 1024 ? 350 : 380;

      calculatedWidth = Math.max(minWidth, Math.min(calculatedWidth, maxWidth));

      // Ensure tooltip doesn't exceed viewport width
      if (calculatedWidth > viewportWidth - 32) {
        calculatedWidth = viewportWidth - 32; // 32px for padding on both sides
      }

      setTooltipWidth(calculatedWidth);

      // Calculate left position, ensuring tooltip doesn't go off-screen
      let left = rect.left + window.scrollX;
      if (left + calculatedWidth > viewportWidth) {
        left = viewportWidth - calculatedWidth - 16; // 16px padding from edge
      }
      if (left < 16) {
        left = 16; // 16px padding from edge
      }

      setTooltipPosition({
        top: rect.bottom + window.scrollY + 16,
        left: left,
      });
    }
  }, [showTooltip]);

  // Check if tooltip should be shown (first visit)
  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('moduleTooltipSeen');
    if (!hasSeenTooltip && modules.length > 0 && !isLoading) {
      setShowTooltip(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, isLoading]);

  // Prevent body scroll when tooltip is shown
  useEffect(() => {
    if (showTooltip) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Disable scrolling
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore original overflow when tooltip is dismissed
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showTooltip]);

  // Update tooltip position when shown or on scroll/resize
  useEffect(() => {
    if (showTooltip && firstCardRef.current) {
      updateTooltipPosition();

      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showTooltip, modules, updateTooltipPosition]);

  // Handle tooltip dismissal
  const handleTooltipDismiss = () => {
    setShowTooltip(false);
    localStorage.setItem('moduleTooltipSeen', 'true');
  };

  // Handle click outside tooltip
  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event) => {
      if (!event.target.closest('.module-tooltip') && !event.target.closest('.first-module-card')) {
        handleTooltipDismiss();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  const handleAddModule = () => {
    setAddModuleErrors({ title: '', description: '' });
    setUploadError('');
    setUploadingImage(false);
    setShowAddModal(true);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setUploadError('');
      setUploadingImage(true);
      setNewModule({
        ...newModule,
        image: file,
        imagePreview: previewUrl,
        thumbnail_url: '', // Clear URL when file is selected
      });

      try {
        const uploadedUrl = await uploadImage(file);
        setNewModule(prev => ({ ...prev, imagePreview: uploadedUrl, thumbnail_url: uploadedUrl }));
        setUploadError('');
      } catch (error) {
        //   console.error('Image upload failed:', error);
        setUploadError(error.message || 'Failed to upload image. Please try again.');
        setNewModule(prev => ({ ...prev, image: null, imagePreview: null, thumbnail_url: '' }));
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleSourceSwitch = (source, isEditing = false) => {
    if (isEditing) {
      setEditingModule(prev => ({ ...prev, imageSource: source }));
    } else {
      setNewModule(prev => ({ ...prev, imageSource: source }));
    }
  };

  const handleThumbnailUrlChange = (e, isEditing = false) => {
    const url = e.target.value;
    if (isEditing) {
      // Clean up previous object URL if it existed
      if (editingModule.imagePreview && editingModule.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(editingModule.imagePreview);
      }
      setEditingModule(prev => ({
        ...prev,
        thumbnail_url: url,
        image: null,
        imagePreview: url || null,
      }));
    } else {
      // Clean up previous object URL if it existed
      if (newModule.imagePreview && newModule.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(newModule.imagePreview);
      }
      setNewModule(prev => ({
        ...prev,
        thumbnail_url: url,
        image: null,
        imagePreview: url || null,
      }));
    }
  };

  const handleAddModuleSubmit = async () => {
    // Reset errors
    const errors = {
      title: '',
      description: '',
    };

    // Validate fields
    let hasErrors = false;
    if (!newModule.title.trim()) {
      errors.title = 'Module title is required';
      hasErrors = true;
    }
    if (!newModule.description.trim()) {
      errors.description = 'Description is required';
      hasErrors = true;
    }

    if (hasErrors) {
      setAddModuleErrors(errors);
      return;
    }

    // Clear errors if validation passes
    setAddModuleErrors({ title: '', description: '' });

    setAdding(true);
    try {
      // Use uploaded URL from imagePreview or thumbnail_url
      const thumbnailUrl = newModule.imagePreview || newModule.thumbnail_url || '';

      await createModule({
        title: newModule.title,
        description: newModule.description,
        thumbnail_url: thumbnailUrl,
        order: parseInt(newModule.order) || 1,
        tags: Array.isArray(newModule.tags) ? newModule.tags : [],
        grouping_ids: toGroupingIds(newModule.grouping_ids),
      });

      // Clean up object URL if it exists
      if (newModule.imagePreview && newModule.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(newModule.imagePreview);
      }

      setShowAddModal(false);
      setNewModule({
        title: '',
        description: '',
        thumbnail_url: '',
        order: 1,
        tags: [],
        grouping_ids: [],
        image: null,
        imagePreview: null,
        imageSource: 'file',
      });
      setIsLoading(true);
      await loadModules();
    } catch (err) {
      // console.error('Error adding module:', err);
      alert(err.message || 'Failed to add module. Please try again.');
    } finally {
      setAdding(false);
      setIsLoading(false);
    }
  };

  const cancelAddModule = () => {
    // Clean up image preview URL if it exists (only for blob URLs)
    if (newModule.imagePreview && newModule.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(newModule.imagePreview);
    }
    setShowAddModal(false);
    setNewModule({
      title: '',
      description: '',
      thumbnail_url: '',
      order: 1,
      tags: [],
      grouping_ids: [],
      image: null,
      imagePreview: null,
      imageSource: 'file',
    });
    setAddModuleErrors({ title: '', description: '' });
  };

  const handleEdit = (moduleId) => {
    const module = modules.find((m) => m.id === moduleId || m.module_id === moduleId || m._id === moduleId);
    if (module) {
      // Get module ID from multiple possible fields
      const modId = module.module_id || module.id || module._id || moduleId;

      // Get image URL from multiple possible fields, ensuring we get a valid URL string
      const thumbnailUrl = (module.thumbnail_url && String(module.thumbnail_url).trim()) ||
        (module.image && String(module.image).trim()) ||
        '';

      setEditingModule({
        id: modId,
        module_id: modId ? String(modId) : null,
        title: module.title || module.name,
        description: module.description || '',
        thumbnail_url: thumbnailUrl,
        order: module.order || 1,
        is_active: module.is_active !== undefined ? module.is_active : true,
        tags: Array.isArray(module.tags) ? module.tags : [],
        grouping_ids: toGroupingIds(module.grouping_ids),
        image: null,
        imagePreview: thumbnailUrl || null,
        imageSource: thumbnailUrl ? 'url' : 'file',
      });
      setShowEditModal(true);
    }
  };

  const handleEditImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setEditingUploadError('');
      setEditingUploadingImage(true);
      setEditingModule({
        ...editingModule,
        image: file,
        imagePreview: previewUrl,
        thumbnail_url: '', // Clear URL when file is selected
      });

      try {
        const uploadedUrl = await uploadImage(file);
        setEditingModule(prev => ({ ...prev, imagePreview: uploadedUrl, thumbnail_url: uploadedUrl }));
        setEditingUploadError('');
      } catch (error) {
        //   console.error('Image upload failed:', error);
        setEditingUploadError(error.message || 'Failed to upload image. Please try again.');
        setEditingModule(prev => ({ ...prev, image: null, imagePreview: null, thumbnail_url: '' }));
      } finally {
        setEditingUploadingImage(false);
      }
    }
  };

  const handleEditThumbnailUrlChange = (e) => {
    handleThumbnailUrlChange(e, true);
  };

  const handleUpdateModuleSubmit = async () => {
    if (!editingModule.title.trim() || !editingModule.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Get module_id from editingModule, fallback to id if module_id is not set
    const moduleId = editingModule.module_id || editingModule.id;

    if (!moduleId) {
      alert('Module ID is missing. Please try again.');
      return;
    }

    setEditing(true);
    try {
      // Use uploaded URL from imagePreview or thumbnail_url
      const thumbnailUrl = editingModule.imagePreview || editingModule.thumbnail_url || '';

      await updateModule({
        module_id: String(moduleId),
        title: editingModule.title,
        description: editingModule.description,
        thumbnail_url: thumbnailUrl,
        order: parseInt(editingModule.order) || 1,
        is_active: editingModule.is_active,
        tags: Array.isArray(editingModule.tags) ? editingModule.tags : [],
        grouping_ids: toGroupingIds(editingModule.grouping_ids),
      });

      // Clean up object URL if it exists
      if (editingModule.imagePreview && editingModule.imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(editingModule.imagePreview);
      }

      setShowEditModal(false);
      setEditingModule({
        id: null,
        module_id: null,
        title: '',
        description: '',
        thumbnail_url: '',
        order: 1,
        is_active: true,
        tags: [],
        grouping_ids: [],
        image: null,
        imagePreview: null,
        imageSource: 'file',
      });
      // Reload modules list (force list loader — loadModules often skips isLoading on same page/search)
      setIsLoading(true);
      await loadModules();
    } catch (err) {
      // console.error('Error updating module:', err);
      alert(err.message || 'Failed to update module. Please try again.');
    } finally {
      setEditing(false);
    }
  };

  const cancelEditModule = () => {
    // Clean up image preview URL if it exists and was newly created
    if (editingModule.imagePreview && editingModule.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editingModule.imagePreview);
    }
    setShowEditModal(false);
    setEditingModule({
      id: null,
      module_id: null,
      title: '',
      description: '',
      thumbnail_url: '',
      order: 1,
      is_active: true,
      tags: [],
      grouping_ids: [],
      image: null,
      imagePreview: null,
      imageSource: 'file',
    });
  };

  const handleDelete = (moduleId) => {
    const module = modules.find((m) => m.id === moduleId || m.module_id === moduleId || m._id === moduleId);
    setModuleToDelete(module);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!moduleToDelete) return;

    // Get module ID from multiple possible fields
    const moduleId = moduleToDelete.module_id || moduleToDelete.id || moduleToDelete._id;

    if (!moduleId) {
      alert('Module ID is missing. Please try again.');
      return;
    }

    setDeleting(true);
    try {
      await deleteModule(String(moduleId));
      setShowDeleteModal(false);
      setModuleToDelete(null);
      setIsLoading(true);
      await loadModules();
    } catch (err) {
      // console.error('Error deleting module:', err);
      alert(err.message || 'Failed to delete module. Please try again.');
    } finally {
      setDeleting(false);
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setModuleToDelete(null);
  };

  const handleSaveModuleOrder = async () => {
    setReorderSaveLoading(true);
    try {
      const payload = reorderModules.map((m, index) => ({
        module_id: String(m.id || m.module_id || m._id),
        order: index + 1,
      }));

      // Update each module's order via API
      const updatePromises = payload.map(async (item) => {
        const module = reorderModules.find(
          (m) => String(m.id || m.module_id || m._id) === item.module_id
        );
        if (module) {
          const thumbnailUrl = (module.thumbnail_url && String(module.thumbnail_url).trim()) ||
            (module.image && String(module.image).trim()) ||
            '';

          return updateModule({
            module_id: item.module_id,
            title: module.title || module.name,
            description: module.description || '',
            thumbnail_url: thumbnailUrl,
            order: item.order,
            is_active: module.is_active !== undefined ? module.is_active : true,
          });
        }
      });

      await Promise.all(updatePromises);
      setShowReorderModal(false);
      Toast.success('Module order updated successfully');
      setIsLoading(true);
      await loadModules();
    } catch (err) {
      // console.error(err);
      Toast.error('Failed to save module order');
    } finally {
      setReorderSaveLoading(false);
      setIsLoading(false);
    }
  };

  const handleUploadCSV = async () => {
    if (!selectedFile) {
      Toast.error('Please select a JSON file');
      return;
    }

    setUploadingCsv(true);
    try {

      const res = await uploadQuizCsv(selectedFile);

      // console.log('JSON UPLOAD RESPONSE:', res);

      // backend sends: { message, data }
      const resultData = res?.data || {};

      // ✅ SAVE RESPONSE TO STATE
      setUploadResult({
        successful: resultData.successful || [],
        skipped: resultData.skipped || [],

        error: resultData.error || [],
      });

      setShowUploadModal(false);
      setSelectedFile(null);

      // ✅ OPEN SUMMARY MODAL
      setShowUploadResultModal(true);

      invalidateChaptersCache();
      invalidateLessonCache();
      invalidateLessonContentCache();
      await loadModules(page, debouncedSearch);
    } catch (err) {
      Toast.error(err.message || 'Failed to upload JSON');
    } finally {
      setUploadingCsv(false);
    }
  };

  // ===== Upload Result Modal State =====


  const handleStatusChange = async (moduleId, newStatus) => {
    try {
      const moduleIdStr = String(moduleId);
      const module = modules.find((m) =>
        String(m.id || m.module_id || m._id) === moduleIdStr
      );

      if (!module) {
        alert('Module not found. Please refresh the page.');
        return;
      }

      const thumbnailUrl = (module.thumbnail_url && String(module.thumbnail_url).trim()) ||
        (module.image && String(module.image).trim()) ||
        '';

      await updateModule({
        module_id: moduleIdStr,
        title: module.title || module.name,
        description: module.description || '',
        thumbnail_url: thumbnailUrl,
        order: module.order || 1,
        is_active: newStatus,
      });
      // Close dropdown after selection
      setOpenDropdownId(null);
      setIsLoading(true);
      await loadModules();
    } catch (err) {
      // console.error('Error updating module status:', err);
      alert(err.message || 'Failed to update module status. Please try again.');
    }
  };


  // Pagination range calculation
  const getPaginationRange = () => {
    if (totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(page - 1, 1);
    const rightSiblingIndex = Math.min(page + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let pageNum = startPage; pageNum <= endPage; pageNum += 1) {
      pages.push(pageNum);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const getModuleChildCount = (module) => {
    if (!module || typeof module !== 'object') return null;

    const fromArray = (arr) => (Array.isArray(arr) ? arr.length : null);
    const fromNumber = (n) => (Number.isFinite(Number(n)) ? Number(n) : null);

    return (
      fromArray(module.chapters) ??
      fromArray(module.units) ??
      fromArray(module.lessons) ??
      fromArray(module.children) ??
      fromNumber(module.chapters_count) ??
      fromNumber(module.chapter_count) ??
      fromNumber(module.total_chapters) ??
      fromNumber(module.totalChapters) ??
      fromNumber(module.children_count) ??
      fromNumber(module.child_count) ??
      null
    );
  };

  const SortableModuleItem = ({ module, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: module.id || module.module_id || module._id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };


    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0 ">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>

          {module.thumbnail_url || module.image ? (
            <img
              src={module.thumbnail_url || module.image}
              alt=""
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-brand text-black flex items-center justify-center font-bold flex-shrink-0">
              {(module.title || module.name).charAt(0)}
            </div>
          )}

          <p className="text-black font-medium flex-1 min-w-0 break-words line-clamp-2">
            {module.title || module.name}
          </p>
        </div>

        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };


  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          {/* Header */}
          <div className="mb-4 md:mb-6 relative z-50">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                Module Management
              </h1>
            </div>
          </div>
          <ModuleNav />

          {/* Filters Bar */}
          <div className="mb-5 md:mb-4 mt-4 md:mt-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
              {/* Row 1: Search (full width on mobile) */}
              <div className="flex flex-col gap-2 w-full md:w-auto">
                <div className="relative w-full md:w-40">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 md:py-1.5 text-sm border border-[#ECECEC] focus:outline-none focus:border-gray-300 bg-white"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <ModuleTagFilterSelect
                  value={tagFilter}
                  open={tagFilterOpen}
                  onToggle={() => setTagFilterOpen((prev) => !prev)}
                  onSelect={(next) => {
                    setPage(1);
                    setTagFilter(next);
                    setTagFilterOpen(false);
                  }}
                />

                {/* Mobile Row 2: Buttons in one line (<415px) */}
                <div className="flex items-center justify-between gap-2 w-full md:hidden">
                  <button
                    onClick={handleAddModule}
                    className="add-module-btn-mobile items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    <span className="text-base font-bold">+</span>
                    <span>Add Module</span>
                  </button>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="upload-quiz-btn flex items-center gap-2 px-3 py-2 md:py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    <HiUpload className="w-4 h-4 upload-quiz-icon-default" />
                    <img src={UploadCsvIcon} alt="Upload" className="w-5 h-5 upload-quiz-icon-mobile hidden" style={{ filter: 'brightness(0) invert(1)' }} />
                    <span className="upload-quiz-text">Upload File</span>
                  </button>
                  <button
                    onClick={() => {
                      setReorderModules(modules);
                      setShowReorderModal(true);
                    }}
                    className="edit-order-btn flex items-center gap-2 px-3 py-2 md:py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                  >
                    <img src={EditOrderIcon} alt="Edit Order" className="w-5 h-5 edit-order-icon hidden" style={{ filter: 'brightness(0) invert(1)' }} />
                    <span className="edit-order-text">Edit Order</span>
                  </button>
                </div>
              </div>

              {/* Desktop/Tablet Row 2: Upload & Edit Order & Add Module */}
              <div className="hidden md:flex items-center justify-end gap-2 w-full md:w-auto">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="upload-quiz-btn flex items-center gap-2 px-3 py-2 md:py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                >
                  <HiUpload className="w-4 h-4 upload-quiz-icon-default" />
                  <img src={UploadCsvIcon} alt="Upload" className="w-5 h-5 upload-quiz-icon-mobile hidden" style={{ filter: 'brightness(0) invert(1)' }} />
                  <span className="upload-quiz-text">Upload File</span>
                </button>
                <button
                  onClick={() => {
                    setReorderModules(modules);
                    setShowReorderModal(true);
                  }}
                  className="edit-order-btn flex items-center gap-2 px-3 py-2 md:py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                >
                  <img src={EditOrderIcon} alt="Edit Order" className="w-5 h-5 edit-order-icon hidden" style={{ filter: 'brightness(0) invert(1)' }} />
                  <span className="edit-order-text">Edit Order</span>
                </button>

                {/* Add Module Button (Visible only on screens > 415px) */}
                <button
                  onClick={handleAddModule}
                  className="add-module-btn-desktop items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                >
                  <span className="text-base font-bold">+</span>
                  <span>Add Module</span>
                </button>
              </div>
            </div>
          </div>


          {/* Card View - Responsive Grid */}
          <div>
            {isLoading || paginationLoading ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-40 flex items-center justify-center">
                <div className="flex items-center gap-3" role="status" aria-live="polite">
                  <svg
                    className="w-12 h-12 animate-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E9D5FF" />
                        <stop offset="25%" stopColor="#C084FC" />
                        <stop offset="50%" stopColor="#A855F7" />
                        <stop offset="75%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#6B21A8" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="url(#purple-gradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="60 200"
                      fill="none"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                </div>
              </div>
            ) : modules.length === 0 ? (
              <div className="w-full min-h-[600px] flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                  {hasAnyModules && String(debouncedSearch || '').trim().length > 0 ? (
                    <div className="text-center text-gray-500">
                      No modules found. Try a different keyword.
                    </div>
                  ) : (
                    <>
                      <div className="mb-6">
                      <svg
                          className="w-24 h-24 mx-auto text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No modules added yet
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Start by adding the first module
                      </p>
                      <button
                        onClick={handleAddModule}
                        className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                      >
                        <span className="text-base">+</span>
                        <span>Add Module</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-4">
                {modules.map((module, index) => {
                  const isActive = module.is_active !== undefined ? module.is_active : true;
                  const moduleId = module.id || module.module_id || module._id;
                  const showDropdown = openDropdownId === moduleId;
                  const isFirstCard = index === 0;
                  const isDisabled = showTooltip && !isFirstCard;
                  const childCount = getModuleChildCount(module);

                  return (
                    <div
                      key={moduleId}
                      ref={isFirstCard ? firstCardRef : null}
                      onClick={(e) => {
                        if (
                          e.target.closest('button') ||
                          e.target.closest('.status-dropdown-container') ||
                          isDisabled
                        ) {
                          return;
                        }

                        const moduleName = module.title || module.name;
                        navigate(`/module/${moduleId}/chapters`, {
                          state: {
                            fromName: moduleName,
                            moduleName,
                          },
                        });
                      }}

                      className={`bg-white rounded-xl p-4 sm:p-5 transition-shadow relative first-module-card overflow-hidden cursor-pointer ${isDisabled ? 'opacity-40 blur-[1px] pointer-events-none cursor-not-allowed' : ''
                        }`}
                      style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                    >
                      {/* Row 1: Icon, Title */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Module Icon */}
                          {module.thumbnail_url || module.image ? (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              <img
                                src={module.thumbnail_url || module.image}
                                alt={module.title || module.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-brand flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                              {(module.title || module.name || 'M').charAt(0).toUpperCase()}
                            </div>
                          )}
                          {/* Separator Line */}
                          <div className="h-10 w-px bg-[#F2E2F7] flex-shrink-0"></div>
                          {/* Module Title */}
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate flex-1">
                            {module.title || module.name}
                          </h3>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const moduleName = module.title || module.name;
                            navigate(`/module/${moduleId}/chapters`, {
                              state: {
                                fromName: moduleName,
                                moduleName,
                              },
                            });
                          }}
                          className="cursor-pointer hover:text-gray-600 transition-colors "
                          title="Go to module chapters"
                        >
                          <HiArrowLeft className={`w-4 h-4 flex-shrink-0 rotate-180 text-gray-400`} />
                        </button>
                      </div>

                      {/* Row 2: Description */}
                      <div className="mb-3">
                        <p className="text-xs sm:text-sm text-gray-500 line-clamp-2  overflow-hidden">
                          {module.description || 'No description available'}
                        </p>
                        {Array.isArray(module.tags) && module.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {module.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-[#EFEBF3] text-brand"
                              >
                                {dashboardModuleTagLabel(tag)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="relative z-10 flex flex-wrap items-center justify-between gap-2">
                        <div className="px-2 py-1 text-[11px] font-semibold rounded-lg bg-gray-100 text-gray-700 flex-shrink-0">
                          Chapters: <span className="text-gray-900">{childCount ?? 0}</span>
                        </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(moduleId);
                          }}
                          className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(moduleId);
                          }}
                          className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>

                        <div className="relative status-dropdown-container flex-shrink-0 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(showDropdown ? null : moduleId);
                            }}
                            className={`px-2 py-2 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1 min-w-[80px] justify-center ${isActive
                              ? 'bg-[#9FF7C9]/30 text-[#60BC7C]/100 hover:bg-green-600/30 '
                              : 'bg-[#FFF0F0] text-[#F76464]  hover:bg-red-600/10'
                              }`}
                          >
                            <span>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                            <HiChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ml-1 ${showDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showDropdown && (
                            <div className="absolute z-50 mt-1 right-0 w-auto min-w-[83px] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 bottom-full mb-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(moduleId, !isActive);
                                }}
                                className="w-full px-2 py-1 text-xs font-semibold flex items-center justify-center rounded-lg transition-colors bg-[#ECECEC] text-black border border-[#A6A6A6] hover:bg-gray-200"
                              >
                                <span>{isActive ? 'Inactive' : 'Active'}</span>
                              </button>
                            </div>
                          )
                          }
                        </div>
                      </div>
                      </div>
                      {/* Round Image at Bottom Right */}
                      <div className="absolute  -mr-4 bottom-0 right-0 opacity-15 pointer-events-none">
                        <img
                          src={require('../../assets/round.png')}
                          alt="Round decoration"
                          className="w-28 h-28 sm:w-[150px] sm:h-[120px] object-contain"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {((totalPages > 1) || (totalPages === 1 && totalCount === 16)) && !isLoading && !paginationLoading && (
              <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((pageNum, idx) =>
                      pageNum === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${pageNum === page
                              ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                              : 'text-gray-900 hover:bg-gray-100'
                            }`}
                          aria-current={pageNum === page ? 'page' : undefined}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* First Visit Tooltip */}
          {showTooltip && firstCardRef.current && modules.length > 0 && (
            <div className="fixed inset-0 z-50 pointer-events-none">
              <div
                className="module-tooltip absolute pointer-events-auto"
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                  transform: 'translateX(0)',
                }}
              >
                {/* Tooltip Arrow - Triangle shape (half visible) */}
                <div
                  className="absolute -top-3 left-6"
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '12px solid transparent',
                    borderRight: '12px solid transparent',
                    borderBottom: '12px solid white',

                  }}
                />

                {/* Tooltip Content */}
                <div
                  className="bg-white text-[#212121] rounded-xl relative"
                  style={{
                    width: `${tooltipWidth}px`,
                    padding: '16px',
                  }}
                >
                  <button
                    onClick={handleTooltipDismiss}
                    className="absolute top-4 right-4 text-sm text-brand font-medium hover:opacity-70 transition-opacity cursor-pointer underline"
                  >
                    Got it
                  </button>
                  <h3 className="text-base font-semibold mb-2 text-[#212121]">👉 Start Here</h3>
                  <p className="text-sm text-[#212121]">
                    Manage lessons and quizzes inside this module.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Module Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add New Module</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowAddModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Module Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newModule.title}
                  onChange={(e) => {
                    setNewModule({ ...newModule, title: e.target.value });
                    if (addModuleErrors.title) {
                      setAddModuleErrors({ ...addModuleErrors, title: '' });
                    }
                  }}
                  placeholder="Enter module title"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${addModuleErrors.title
                    ? 'border-red-500'
                    : 'border-gray-300'
                    }`}
                />
                {addModuleErrors.title && (
                  <p className="mt-1 text-xs text-red-500">{addModuleErrors.title}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newModule.description}
                  onChange={(e) => {
                    setNewModule({ ...newModule, description: e.target.value });
                    if (addModuleErrors.description) {
                      setAddModuleErrors({ ...addModuleErrors, description: '' });
                    }
                  }}
                  placeholder="Enter module description"
                  rows="2"
                  className={`w-full px-3 py-1 text-sm border rounded-lg focus:outline-none resize-none ${addModuleErrors.description
                    ? 'border-red-500'
                    : 'border-gray-300'
                    }`}
                />
                {addModuleErrors.description && (
                  <p className="mt-1 text-xs text-red-500">{addModuleErrors.description}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Thumbnail Image
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60] sm:whitespace-nowrap leading-tight inline-block ${newModule.imageSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'}`}>
                        {newModule.imageSource === 'url'
                          ? 'Switch right to choose a file from your device'
                          : 'Switch left to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSourceSwitch(newModule.imageSource === 'url' ? 'file' : 'url')}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${newModule.imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${newModule.imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {newModule.imageSource === 'url' ? (
                    <div>
                      <input
                        type="url"
                        value={newModule.thumbnail_url}
                        onChange={handleThumbnailUrlChange}
                        placeholder="Enter image URL"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                      />
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={uploadingImage}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none text-sm ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />

                      {uploadingImage && (
                        <div className="mt-2 flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-xs text-gray-600">Uploading image...</span>
                        </div>
                      )}
                      {uploadError && (
                        <p className="mt-1 text-xs text-red-500">{uploadError}</p>
                      )}
                    </div>
                  )}

                  {/* Preview */}
                  {(newModule.imagePreview || newModule.thumbnail_url) && (
                    <div className="w-full h-36 rounded-lg overflow-hidden border border-gray-200 relative group">
                      <img
                        src={newModule.imagePreview || newModule.thumbnail_url}
                        alt="Thumbnail Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextElementSibling;
                          if (errorDiv) {
                            errorDiv.classList.remove('hidden');
                            errorDiv.classList.add('flex');
                          }
                        }}
                      />
                      <div className="hidden absolute inset-0 items-center justify-center bg-gray-100 rounded-lg">
                        <p className="text-xs text-gray-500">Failed to load image</p>
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() =>
                          setNewModule({
                            ...newModule,
                            image: null,
                            imagePreview: null,
                            thumbnail_url: '',
                          })
                        }
                        className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <ModuleTagsField
                tags={newModule.tags}
                onChange={(tags) => setNewModule({ ...newModule, tags })}
              />
              <ModuleGroupingsField
                groupingIds={newModule.grouping_ids}
                options={groupingOptions}
                onChange={(grouping_ids) => setNewModule({ ...newModule, grouping_ids })}
              />
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={newModule.order}
                  onChange={(e) => setNewModule({ ...newModule, order: e.target.value })}
                  placeholder="1"
                  min="1"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none "
                />
              </div> */}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={cancelAddModule}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAddModuleSubmit}
                disabled={adding || uploadingImage}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  'Add Module'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Module Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Edit Module</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowEditModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Module Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingModule.title}
                  onChange={(e) => setEditingModule({ ...editingModule, title: e.target.value })}
                  placeholder="Enter module title"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editingModule.description}
                  onChange={(e) => setEditingModule({ ...editingModule, description: e.target.value })}
                  placeholder="Enter module description"
                  rows="2"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none"
                />
              </div>
              {/* Thumbnail Image */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Thumbnail Image
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 group relative">
                      <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
                      <span className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60] sm:whitespace-nowrap leading-tight inline-block ${editingModule.imageSource === 'url' ? 'w-[185px] sm:w-fit sm:max-w-[290px]' : 'w-[175px] sm:w-fit sm:max-w-[240px]'}`}>
                        {editingModule.imageSource === 'url'
                          ? 'Switch right to choose a file from your device'
                          : 'Switch left to paste an image URL.'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSourceSwitch(editingModule.imageSource === 'url' ? 'file' : 'url', true)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${editingModule.imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${editingModule.imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  {editingModule.imageSource === 'url' ? (
                    <div>
                      <input
                        type="url"
                        value={editingModule.thumbnail_url}
                        onChange={handleEditThumbnailUrlChange}
                        placeholder="Enter image URL"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                      />
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleEditImageChange}
                        disabled={editingUploadingImage}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none text-sm ${editingUploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />

                      {editingUploadingImage && (
                        <div className="mt-2 flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-xs text-gray-600">Uploading image...</span>
                        </div>
                      )}
                      {editingUploadError && (
                        <p className="mt-1 text-xs text-red-500">{editingUploadError}</p>
                      )}
                    </div>
                  )}

                  {/* Preview */}
                  {(editingModule.imagePreview || editingModule.thumbnail_url) && (
                    <div className="w-full h-36 rounded-lg overflow-hidden border border-gray-200 relative group">
                      <img
                        src={editingModule.imagePreview || editingModule.thumbnail_url}
                        alt="Thumbnail Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = e.target.nextElementSibling;
                          if (errorDiv) {
                            errorDiv.classList.remove('hidden');
                            errorDiv.classList.add('flex');
                          }
                        }}
                      />
                      <div className="hidden absolute inset-0 items-center justify-center bg-gray-100 rounded-lg">
                        <p className="text-xs text-gray-500">Failed to load image</p>
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() =>
                          setEditingModule({
                            ...editingModule,
                            image: null,
                            imagePreview: null,
                            thumbnail_url: '',
                          })
                        }
                        className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <ModuleTagsField
                tags={editingModule.tags}
                onChange={(tags) => setEditingModule({ ...editingModule, tags })}
                isEditing
              />
              <ModuleGroupingsField
                groupingIds={editingModule.grouping_ids}
                options={groupingOptions}
                onChange={(grouping_ids) => setEditingModule({ ...editingModule, grouping_ids })}
                isEditing
              />
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={editingModule.order}
                  onChange={(e) => setEditingModule({ ...editingModule, order: e.target.value })}
                  placeholder="1"
                  min="1"
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none"
                />
              </div> */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingModule.is_active}
                    onChange={(e) => setEditingModule({ ...editingModule, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-xs font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={cancelEditModule}
                disabled={editing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateModuleSubmit}
                disabled={editing || editingUploadingImage}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editing ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : (
                  'Update Module'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && moduleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800">Delete Module</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowDeleteModal(false);
                  setOpenDropdownId(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-4 ">
              <p className="text-gray-700 ">
                Are you sure you want to delete the module.
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder Modules
              </h2>
              {/* <button
                onClick={() => setShowReorderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button> */}
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop modules to reorder them. Click <b>Save Order</b> to apply.
            </p>

            {/* Drag List */}
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={() => {
                // Prevent horizontal scrolling during drag
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                // Restore scrolling after drag
                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderModules((prev) => {
                  const oldIndex = prev.findIndex(
                    (m) => (m.id || m.module_id || m._id) === active.id
                  );
                  const newIndex = prev.findIndex(
                    (m) => (m.id || m.module_id || m._id) === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                // Restore scrolling if drag is cancelled
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderModules.map(
                  (m) => m.id || m.module_id || m._id
                )}
                strategy={verticalListSortingStrategy}
              >
                {/* Calculate height: each item is ~72px (p-4 = 16px top/bottom + content ~40px) + gap-3 (12px) = ~84px per item, 5 items = ~420px */}
                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderModules.map((module, index) => (
                    <SortableModuleItem
                      key={module.id || module.module_id || module._id}
                      module={module}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveModuleOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload JSON Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Upload Content via JSON</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a JSON file containing your learning content. Ensure the file follows the required format for successful processing.
              </p>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-700">Select JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand/10 file:text-brand hover:file:bg-brand/20 cursor-pointer"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                disabled={uploadingCsv}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadCSV}
                disabled={uploadingCsv || !selectedFile}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploadingCsv ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Uploading...
                  </>
                ) : (
                  'Upload'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {/* Upload Result Modal */}
      {showUploadResultModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full md:ml-0 lg:ml-8 xl:ml-0 overflow-hidden">

            {/* ===== HEADER ===== */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">
                JSON Upload Summary
              </h2>
              <button
                onClick={() => setShowUploadResultModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>

            {/* ===== BODY ===== */}
            <div className="px-6 py-5 space-y-4">

              {/* -------- SUCCESS SECTION -------- */}
              <div className="border rounded-xl overflow-hidden">
                <button
                  onClick={() =>
                    setOpenSection(openSection === 'success' ? null : 'success')
                  }
                  className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition"
                >
                  <span className="font-semibold text-green-700">
                    Successfully Added ({uploadResult.successful.length})
                  </span>
                  <span
                    className={`transform transition ${openSection === 'success' ? 'rotate-180' : ''
                      }`}
                  >
                    <HiChevronDown
                      className={`w-5 h-5 text-green-700 transform transition-transform duration-500 ease-in-out ${openSection === 'success' ? 'rotate-30' : ''
                        }`}
                    />
                  </span>
                </button>
                <div
                  className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all duration-200 ease-in-out ${openSection === 'success' && uploadResult.successful.length > 0
                      ? 'max-h-40 opacity-100 py-3'
                      : 'max-h-0 opacity-0 py-0'
                    }`}
                >
                  <ul className="text-sm text-green-700 space-y-2">
                    {uploadResult.successful.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">
                          Row {item.row}:
                        </span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Chapter: {item.chapter}- {item.chapter_name}</div>
                          <div>Lesson: {item.lesson}- {item.lesson_name}</div>
                          <div>Content: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.question_text}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

              </div>

              {/* -------- SKIPPED SECTION -------- */}
              <div className="border rounded-xl overflow-hidden">
                <button
                  onClick={() =>
                    setOpenSection(openSection === 'skipped' ? null : 'skipped')
                  }
                  className="w-full flex items-center justify-between px-4 py-3 bg-yellow-50 hover:bg-yellow-100 transition"
                >
                  <span className="font-semibold text-yellow-700">
                    Skipped Records ({uploadResult.skipped.length})
                  </span>

                  <HiChevronDown
                    className={`w-5 h-5 text-yellow-700 transform transition-transform duration-500 ease-in-out ${openSection === 'skipped' ? 'rotate-180' : 'rotate-0'
                      }`}
                  />
                </button>

                {/* 🔥 ALWAYS RENDERED – only animated */}
                <div
                  className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all duration-200 ease-in-out ${openSection === 'skipped' && uploadResult.skipped.length > 0
                      ? 'max-h-40 opacity-100 py-3'
                      : 'max-h-0 opacity-0 py-0'
                    }`}
                >
                  <ul className="text-sm text-gray-700 space-y-2">
                    {uploadResult.skipped.map((item, i) => (
                      <li key={i} className="flex flex-col gap-0.5">
                        <span className=" font-medium text-gray-700">
                          <span className="text-yellow-700">  Row {item.row}</span>
                          {item.json_path ? (
                            <> — {formatJsonPath(item.json_path)}</>
                          ) : (item.content != null || item.content_name) ? (
                            <> — Content: {item.content_name ?? item.content}</>
                          ) : null}
                        </span>
                        <span className=" text-gray-700">
                          {item.reason || item.error || item.message || 'Skipped by server'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>


              {/* -------- ERROR PROMPT -------- */}
              {uploadResult.error.length > 0 && (

                <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50">

                  {/* -------- HEADER BUTTON -------- */}
                  <button
                    onClick={() =>
                      setOpenSection(openSection === 'error' ? null : 'error')
                    }
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-100 transition"
                  >
                    <span className="font-semibold text-red-700">
                      Errors found during upload ({uploadResult.error.length})
                    </span>

                    <HiChevronDown
                      className={`w-5 h-5 text-red-700 transform transition-transform duration-1200 ease-in-out ${openSection === 'error' ? 'rotate-180' : 'rotate-0'
                        }`}
                    />
                  </button>

                  {/* -------- DROPDOWN CONTENT (always rendered) -------- */}
                  <ul
                    className={`px-4 text-sm text-red-700 space-y-2 overflow-hidden list-disc list-inside transition-all duration-1500 ease-in-out overflow-y-auto ${openSection === 'error'
                        ? 'max-h-40 opacity-100 py-3'
                        : 'max-h-0 opacity-0 py-0'
                      }`}
                  >
                    {uploadResult.error.map((item, i) => (
                      <li key={i} className="flex flex-col gap-0.5">
                        <span className="font-medium">
                          Row {item.row}
                          {item.json_path ? (
                            <> — {formatJsonPath(item.json_path)}</>
                          ) : (item.content != null || item.content_name) ? (
                            <> — Content: {item.content_name ?? item.content}</>
                          ) : null}
                        </span>
                        <span className="text-red-600">
                          {item.error || item.reason || item.message || 'Unknown server error'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}



              {/* -------- FOOTER TEXT -------- */}
              {/* <p className="text-sm text-gray-600 text-center pt-2">
          CSV processing completed. Review the results above.
        </p> */}
            </div>

            {/* ===== FOOTER ===== */}
            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => {
                  setShowUploadResultModal(false);
                  setOpenSection(null);
                }}
                className="px-5 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Module;

